import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaX } from 'react-icons/fa6';
import './Onboarding.css';

// The way in.
//
// Two halves, full bleed, no cards. On the left, a page of paper with the
// machine writing on it: it explains itself, a few words at a time, offering
// its own next words in grey and then taking them. It never stops, and when
// the page fills it scrolls, and when the piece ends a fresh sheet goes in.
//
// On the right, one question at a time. The brief used to be a modal over the
// app, which meant the first thing anyone saw of ITYPE was a dialog. Now it is
// the same screen you arrived on: title, two ways in, and then the questions,
// with the machine still typing beside you.

const KEY = 'itype-brief';
const MAX_IMAGES = 4;
const MAX_EDGE = 1024;   // a photograph needs no more than this to be understood
const MAX_NOTES = 12000;

export const readBrief = () => {
  try {
    return JSON.parse(localStorage.getItem(KEY) || 'null') || {};
  } catch {
    return {};
  }
};

const empty = { topic: '', voice: '', notes: '', images: [] };

// What the machine writes about itself. Each line is what it types, then what
// it offers, then what it does with the offer: 'take' keeps it, 'leave' drops
// it, because half the point is that you are not obliged to accept anything.
const SCRIPT = [
  { typed: 'This is ITYPE.', offer: ' A typewriter that thinks.', end: 'take' },
  { typed: '\n\nYou write. It reads what you have written and offers', offer: ' the next few words, in grey, like this.', end: 'take' },
  { typed: '\n\nThe right arrow takes one letter. Ctrl and the right arrow takes', offer: ' a whole word.', end: 'take' },
  { typed: ' Ctrl, shift and right takes the lot.', offer: ' Nothing arrives without you asking.', end: 'take' },
  { typed: '\n\nDo not like what it said? The up arrow', offer: ' asks for another one, and another.', end: 'take' },
  { typed: '\n\nIt never writes you a paragraph.', offer: ' It writes eight words at most, then stops and waits.', end: 'leave' },
  { typed: ' A few words is help. A paragraph is somebody else writing your letter.', offer: '', end: 'take' },
  { typed: '\n\nTell it what you are writing first, over on the right, and every', offer: ' suggestion after that is about your subject.', end: 'take' },
  { typed: '\n\nAlt and F takes everything off the screen but the words.', offer: ' Ctrl and S saves it, with a title written for it.', end: 'take' },
  { typed: '\n\nThat is the whole machine. Your turn.', offer: '', end: 'take' },
];

const TYPE_MS = 34;      // per character
const THINK_MS = 520;    // the beat where a request would be in flight
const READ_MS = 1080;    // long enough to read what was offered
const AFTER_MS = 420;    // and a breath before the next line
const REST_MS = 3200;    // at the end of the piece, before a fresh sheet

// Types the script out forever: typing, thinking, offering, taking, next.
const useSelfWritingPage = () => {
  const [done, setDone] = useState('');       // everything already committed
  const [chars, setChars] = useState(0);      // of the current line
  const [step, setStep] = useState(0);
  const [phase, setPhase] = useState('typing');

  const line = SCRIPT[step] || SCRIPT[SCRIPT.length - 1];

  useEffect(() => {
    let timer;
    if (phase === 'typing') {
      if (chars < line.typed.length) {
        timer = setTimeout(() => setChars((n) => n + 1), TYPE_MS);
      } else {
        timer = setTimeout(() => setPhase(line.offer ? 'offered' : 'next'), THINK_MS);
      }
    } else if (phase === 'offered') {
      timer = setTimeout(() => setPhase(line.end === 'take' ? 'taken' : 'left'), READ_MS);
    } else if (phase === 'taken' || phase === 'left') {
      timer = setTimeout(() => setPhase('next'), AFTER_MS);
    } else if (phase === 'next') {
      const last = step === SCRIPT.length - 1;
      timer = setTimeout(() => {
        if (last) {
          // A fresh sheet, rather than the same lines shuffled forever.
          setDone('');
          setStep(0);
        } else {
          setDone((text) => text + line.typed + (line.end === 'take' ? line.offer : ''));
          setStep((n) => n + 1);
        }
        setChars(0);
        setPhase('typing');
      }, last ? REST_MS : 60);
    }
    return () => clearTimeout(timer);
  }, [phase, chars, line, step]);

  return {
    done,
    typing: line.typed.slice(0, chars),
    offer: line.offer,
    phase,
  };
};

// Downscale in the browser: the brief travels with every request, and a four
// megapixel photograph would make every suggestion slower than typing.
const toInlineImage = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = () => reject(new Error('could not read'));
  reader.onload = () => {
    const img = new Image();
    img.onerror = () => reject(new Error('not an image'));
    img.onload = () => {
      const scale = Math.min(1, MAX_EDGE / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.82);
      resolve({ mimeType: 'image/jpeg', data: dataUrl.split(',')[1], preview: dataUrl, name: file.name });
    };
    img.src = reader.result;
  };
  reader.readAsDataURL(file);
});

const readTextFile = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = () => reject(new Error('could not read'));
  reader.onload = () => resolve(String(reader.result || ''));
  reader.readAsText(file);
});

const QUESTIONS = [
  {
    field: 'topic',
    label: 'What are you writing?',
    help: 'A sentence is plenty. This is the one that changes the most.',
    placeholder: 'A letter to my landlord about the heating',
    kind: 'line',
  },
  {
    field: 'voice',
    label: 'How should it sound?',
    help: 'Your voice, not a house style. Short and blunt is a real answer.',
    placeholder: 'Short sentences. Polite, but not sorry.',
    kind: 'line',
  },
  {
    field: 'notes',
    label: 'Anything it should know?',
    help: 'Names, facts, what you last sent them. Drop in documents and images and they go too.',
    placeholder: 'The radiator has been cold since October. Three calls, no visit.',
    kind: 'block',
  },
];

export default function Onboarding({ startAt = 0, hasText, onDone }) {
  const page = useSelfWritingPage();
  const [step, setStep] = useState(startAt);   // 0 is the door, then 1..3
  const [brief, setBrief] = useState(() => ({ ...empty, ...readBrief() }));
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [leaving, setLeaving] = useState(false);

  const paperRef = useRef(null);
  const fileRef = useRef(null);
  const fieldRef = useRef(null);

  // The page keeps its own bottom in view as it fills.
  useEffect(() => {
    const el = paperRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [page.done, page.typing, page.phase]);

  const save = useCallback((next) => {
    setBrief(next);
    try {
      localStorage.setItem(KEY, JSON.stringify(next));
    } catch {
      // A brief too large to store is still usable for this session.
    }
  }, []);

  const leave = useCallback(() => {
    setLeaving(true);
    setTimeout(onDone, 420);
  }, [onDone]);

  const question = QUESTIONS[step - 1];

  useEffect(() => {
    if (!question) return undefined;
    const timer = setTimeout(() => fieldRef.current?.focus(), 90);
    return () => clearTimeout(timer);
  }, [question]);

  const back = useCallback(() => setStep((n) => Math.max(0, n - 1)), []);
  const next = useCallback(() => {
    if (step >= QUESTIONS.length) leave();
    else setStep((n) => n + 1);
  }, [step, leave]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        if (step === 0) setStep(1);
        else next();
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        leave();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [step, next, leave]);

  const takeFiles = useCallback(async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setBusy(true);
    try {
      const images = [];
      let appended = '';
      for (const file of list) {
        if (file.type.startsWith('image/')) {
          try { images.push(await toInlineImage(file)); } catch { /* skip what will not read */ }
        } else if (/\.(txt|md|markdown|csv|json)$/i.test(file.name) || file.type.startsWith('text/')) {
          appended += `${appended ? '\n\n' : ''}${file.name}:\n${await readTextFile(file)}`;
        }
      }
      save({
        ...brief,
        notes: (brief.notes + (brief.notes && appended ? '\n\n' : '') + appended).slice(0, MAX_NOTES),
        images: [...(brief.images || []), ...images].slice(0, MAX_IMAGES),
      });
    } finally {
      setBusy(false);
    }
  }, [brief, save]);

  const images = brief.images || [];
  const stepLabel = useMemo(
    () => (step === 0 ? '' : `${String(step).padStart(2, '0')} / ${String(QUESTIONS.length).padStart(2, '0')}`),
    [step],
  );

  return (
    <div className={`flow${leaving ? ' leaving' : ''}`}>
      {/* Left: the machine, writing about itself. */}
      <section className="flow-stage" aria-hidden="true">
        <div className="flow-paper" ref={paperRef}>
          <div className="flow-paper-inner">
            <p className="flow-page">
              <span className="flow-ink">{page.done}{page.typing}</span>
              {(page.phase === 'offered' || page.phase === 'taken' || page.phase === 'left') && (
                <span className={`flow-offer ${page.phase}`}>{page.offer}</span>
              )}
              {page.phase === 'typing' && <span className="flow-caret" />}
            </p>
          </div>
        </div>
      </section>

      {/* Right: one question at a time. */}
      <section className="flow-panel">
        {step > 0 && (
          <button type="button" className="flow-skip" onClick={leave}>
            <FaX />
            <span>skip</span>
          </button>
        )}

        <div className="flow-panel-inner">
          {step === 0 ? (
            <div className="flow-door" key="door">
              <p className="flow-eyebrow">A typewriter that thinks</p>
              <h1 className="flow-title">
                Write.
                <br />
                It keeps up.
              </h1>
              <p className="flow-lede">
                You type. It offers the next few words. You take as many of them as
                you want, or none. Nothing leaves this device except the sentence it
                is helping you finish.
              </p>

              <div className="flow-doors">
                <button type="button" className="flow-cta primary" onClick={() => setStep(1)}>
                  <span className="flow-cta-name">Start</span>
                  <span className="flow-cta-note">
                    Three questions, and every suggestion after them is about your
                    subject rather than about nothing.
                  </span>
                  <span className="flow-key">enter</span>
                </button>

                <button type="button" className="flow-cta" onClick={leave}>
                  <span className="flow-cta-name">{hasText ? 'Keep writing' : 'Just start writing'}</span>
                  <span className="flow-cta-note">
                    {hasText
                      ? 'Your last page is still here, exactly where you left it.'
                      : 'Straight to the blank page. The questions are on ctrl + b whenever you want them.'}
                  </span>
                  <span className="flow-key">esc</span>
                </button>
              </div>
            </div>
          ) : (
            <div
              className="flow-question"
              key={question.field}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => { e.preventDefault(); setDragging(false); takeFiles(e.dataTransfer.files); }}
            >
              <p className="flow-step">{stepLabel}</p>
              <h2 className="flow-ask">{question.label}</h2>
              <p className="flow-help">{question.help}</p>

              <label className="brief-field">
                <span className="flow-hidden-label">{question.label}</span>
                {question.kind === 'line' ? (
                  <input
                    ref={fieldRef}
                    type="text"
                    value={brief[question.field]}
                    onChange={(e) => save({ ...brief, [question.field]: e.target.value.slice(0, MAX_NOTES) })}
                    placeholder={question.placeholder}
                  />
                ) : (
                  <textarea
                    ref={fieldRef}
                    rows={5}
                    value={brief[question.field]}
                    onChange={(e) => save({ ...brief, [question.field]: e.target.value.slice(0, MAX_NOTES) })}
                    placeholder={question.placeholder}
                  />
                )}
              </label>

              {question.kind === 'block' && (
                <div className={`flow-drop${dragging ? ' dragging' : ''}`}>
                  <button type="button" className="brief-attach" onClick={() => fileRef.current?.click()}>
                    {busy ? 'Reading...' : 'Add documents or images'}
                  </button>
                  <input
                    ref={fileRef}
                    type="file"
                    multiple
                    accept="image/*,.txt,.md,.markdown,.csv,.json,text/*"
                    onChange={(e) => { takeFiles(e.target.files); e.target.value = ''; }}
                    hidden
                  />
                  <span className="flow-drop-hint">or drop them anywhere on this side</span>
                </div>
              )}

              {question.kind === 'block' && images.length > 0 && (
                <div className="brief-images">
                  {images.map((image, i) => (
                    <div className="brief-thumb" key={`${image.name}-${i}`}>
                      <img src={image.preview || `data:${image.mimeType};base64,${image.data}`} alt={image.name || 'attachment'} />
                      <button
                        type="button"
                        onClick={() => save({ ...brief, images: images.filter((_, n) => n !== i) })}
                        aria-label={`Remove ${image.name || 'image'}`}
                      >
                        <FaX />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="flow-nav">
                <button type="button" className="flow-back" onClick={back}>back</button>
                <button type="button" className="flow-next" onClick={next}>
                  {step >= QUESTIONS.length ? 'Start writing' : 'Next'}
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
